import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { OutlinedField } from "@/components/ui/outlined-field";
import { cn } from "@/lib/utils";

// The donor's `InputField` — MUI's outlined `TextField` — as one shared control.
//
// It is the frame (`OutlinedField`) plus a bare `input`: 16px text, 14px of
// horizontal padding, and the 56px height the frame sets. `@/components/ui/input`
// is the vendored shadcn input, which is a different control at a different
// height with its own label conventions; this is the ported one, and it stays
// beside it rather than editing it (`CLAUDE.md`, conventions).
//
// **The value is controlled and reported as a string.** Every donor form field
// is, dimensions included — the donor keeps `dimension_width` as form text and
// only the wire turns it into a decimal — so a caller never has to think about
// `""` versus `null` versus `NaN` on the way through.
//
// Two of the props are whole MUI behaviors rather than attributes, and both
// arrived with the Add Variant dialog (#254):
//
//   - **`multiline`** is MUI's `TextareaAutosize`: a textarea that starts at
//     `minRows` (1, undefined here) and grows with its content instead of
//     scrolling. The donor asks for it on Item Name and Model Number
//     (`AddVariantIsland.tsx:101, 111`), where a variant's name is its parent's
//     name plus a finish and routinely outruns half a dialog's width.
//   - **`min`** brings the donor's `dimensionInputProps` with it
//     (`utils/validation.ts:23-35`): `:out-of-range` paints the input in the
//     error colours. That is the *browser's* range check, not the form's — the
//     spinner can put -1 in the box a beat before `onChange` reports it — so it
//     is a second signal rather than a duplicate of the message below.

export interface TextFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  /**
   * Under the field while there is no error. A node, as the donor's
   * `InputField` takes one: the publish form's counters are a `caption` that
   * turns red past its limit while the field itself stays unerrored
   * (`PublishStepComponent.tsx:136-161`).
   */
  helperText?: ReactNode;
  /** `type="number"` for the dimension fields, which is where the donor gets
   * its spinner and its refusal of letters. */
  type?: "text" | "number";
  /**
   * Passed through for `type="number"`, matching the donor's `min: 0` — and it
   * is what arms the `:out-of-range` painting, which has no other trigger.
   */
  min?: number;
  /**
   * MUI's `multiline`: a self-sizing textarea in place of the input.
   *
   * Ignored under `type="number"`, which has no textarea equivalent — and no
   * donor field asks for both.
   */
  multiline?: boolean;
  /** See `OutlinedField` — the dimension fields' 8px message gap. */
  messageSpacing?: "default" | "loose";
  /**
   * MUI's `size`: `small` is the 40px outlined field, its input padded
   * `8.5px 14px` around MUI's 1.4375em line — the Homesites panel's range
   * bounds (`HomesiteListIsland.tsx:131-160`). Ignored under `multiline`, which
   * no small donor field asks for.
   */
  size?: "small" | "medium";
  /** The donor focuses the first input when a dialog finishes opening
   * (`focusFirstInput`); the field that is first says so. */
  autoFocus?: boolean;
  /** The control's `name` — which the browser keys its autofill history on. */
  name?: string;
  className?: string;
  /**
   * Classes on the outlined frame — MUI's `InputProps` style on the input
   * root, which the publish form's note uses for its `min-height: 75.5px`
   * (`PublishStepComponent.tsx:154`). The frame centres the control, as MUI's
   * root does.
   */
  frameClassName?: string;
  "data-testid"?: string;
}

export function TextField({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  error,
  helperText,
  type = "text",
  min,
  multiline = false,
  messageSpacing = "default",
  size = "medium",
  autoFocus = false,
  name,
  className,
  frameClassName,
  "data-testid": testId,
}: TextFieldProps) {
  const inputId = useId();
  const messageId = useId();
  const [focused, setFocused] = useState(false);
  const message = error ?? helperText;
  const growing = multiline && type === "text";
  const textarea = useRef<HTMLTextAreaElement | null>(null);

  // MUI measures its multiline field with a hidden shadow textarea and sets an
  // explicit height from it. This does the same thing with the real one: drop
  // the height, read what the content wants, and set that.
  //
  // `useEffect` rather than a layout effect because this component renders on
  // the server too, and a layout effect there is a warning and a no-op. The one
  // frame it costs is invisible in practice — the field opens empty, at exactly
  // the one row `rows={1}` already gives it, and every later height follows a
  // keystroke that has already been painted.
  useEffect(() => {
    const element = textarea.current;
    if (element === null) return;

    const syncHeight = () => {
      element.style.height = "auto";
      element.style.height = `${element.scrollHeight}px`;
    };

    syncHeight();

    // **Width changes the wrap, and the wrap changes the height.** Re-measuring
    // on `value` alone leaves a field that was three lines tall at 1200px still
    // three lines tall at 700px, with the fourth clipped by the
    // `overflow-hidden` the explicit height needs. MUI does not have that bug:
    // `TextareaAutosize` observes the textarea *and* listens for window resize
    // (`TextareaAutosize.js:150-172`), so the donor's Description box regrows on
    // a narrowing viewport and this one has to as well.
    //
    // Both, as MUI does: the observer catches a box that changes width without
    // the window moving — the Add Variant dialog's own responsive columns at the
    // `md` breakpoint do exactly that — and the window listener is the
    // fallback where `ResizeObserver` is missing.
    const containerWindow = element.ownerDocument.defaultView ?? window;
    containerWindow.addEventListener("resize", syncHeight);

    if (typeof ResizeObserver === "undefined") {
      return () => containerWindow.removeEventListener("resize", syncHeight);
    }

    // The height this sets is itself a size change, so the observer would
    // re-enter and the browser would log "ResizeObserver loop completed with
    // undelivered notifications". MUI's answer, kept: stop observing, measure,
    // and start again one frame later.
    let frame = 0;
    const observer = new ResizeObserver(() => {
      observer.unobserve(element);
      cancelAnimationFrame(frame);
      syncHeight();
      frame = requestAnimationFrame(() => observer.observe(element));
    });
    observer.observe(element);

    return () => {
      cancelAnimationFrame(frame);
      containerWindow.removeEventListener("resize", syncHeight);
      observer.disconnect();
    };
  }, [value, growing]);

  const controlProps = {
    id: inputId,
    "data-testid": testId,
    autoFocus,
    name,
    value,
    disabled,
    "aria-required": required || undefined,
    "aria-invalid": error ? (true as const) : undefined,
    "aria-describedby": message ? messageId : undefined,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    onChange: (event: { target: { value: string } }) => onChange(event.target.value),
  };

  const controlClassName = cn(
    "text-lennar-ink w-full bg-transparent px-3.5 text-base outline-none",
    // MUI's input is 1.4375em (23px) tall at 16px: the medium input pads
    // 16.5px around it, which this draws as 16 around a 24px line — the same
    // 56px, one row. A textarea grows by the line, so it keeps MUI's own.
    growing
      ? "py-[16.5px] leading-[23px]"
      : size === "small"
        ? "py-[8.5px] leading-[23px]"
        : "py-4 leading-6",
    // **A named parity break, owner-ruled kept** (#542 decision round, D6,
    // 2026-09-22, and #576 item I, which is where it was first reported). MUI
    // leaves a disabled input on the ordinary cursor; Atlas shows
    // `not-allowed`, here and on every other disabled control in the app —
    // several of which are vendored shadcn primitives that ship it by default
    // and are not ours to restyle. Put to the owner as one app-wide question
    // rather than per-control, and ruled kept as one.
    "disabled:cursor-not-allowed",
  );

  return (
    <OutlinedField
      label={label}
      required={required}
      // MUI's outlined input shrinks on **either** a value or focus, and the
      // focus half matters more than it looks: an autofocused empty field (the
      // dialog's Item Name) would otherwise open with its label sitting on top
      // of the caret.
      shrink={value !== "" || focused}
      error={error}
      helperText={helperText}
      disabled={disabled}
      htmlFor={inputId}
      messageId={message ? messageId : undefined}
      messageSpacing={messageSpacing}
      size={growing ? "medium" : size}
      className={className}
      frameClassName={frameClassName}
    >
      {growing ? (
        <textarea
          {...controlProps}
          ref={textarea}
          // The floor the effect above grows from, and the height the field has
          // before that effect has ever run — including on the server.
          rows={1}
          className={cn(controlClassName, "resize-none overflow-hidden")}
        />
      ) : (
        <input
          {...controlProps}
          type={type}
          min={min}
          className={cn(
            controlClassName,
            // The donor's `&:out-of-range`: `error.main` on `error.pale`
            // (`utils/validation.ts:23-35`). `--color-lennar-error` rather than
            // `--destructive` — `error.main` is `red.A400`, #ff1744, and
            // `--destructive` is a different red that would also invert under a
            // dark mode the donor does not have.
            //
            // No radius on the fill: the donor's `input` has none, so its tint
            // squares off inside the frame's own 4px, and rounding it here would
            // be a nicer-looking divergence.
            //
            // A CSS pseudo-class rather than a prop, because that is what the
            // donor uses and what it buys: the browser evaluates it against the
            // live `min`, so a keystroke repaints the field without a render,
            // and a field with no `min` can never match it.
            "out-of-range:text-lennar-error out-of-range:bg-lennar-error-pale",
          )}
        />
      )}
    </OutlinedField>
  );
}
